import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { Plus } from "lucide-react-native";
import { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Props for the AddColumnButtonView component
 */
interface AddColumnButtonViewProps {
  /**
   * Callback function invoked when the add column button is pressed
   */
  onPress: () => void;
}

/**
 * View component that renders a button to add a new column to the Kanban board.
 *
 * SE-6554 restyle — the placeholder now speaks the board's own "add" language.
 * It used to be a 2px dashed outline around a grey filled circle holding a
 * Lucide glyph painted with a LITERAL `#A1A1AA`, under a `@ts-expect-error`.
 * That hex is a fixed mid-grey: it does not move between light and dark, so the
 * icon drifted from every themed neighbour, and the heavy circle read as a
 * disabled avatar slot rather than a call to action.
 *
 * What replaces it is the `+ Add stage` affordance from `StageEditorView`,
 * which is this board's established "add a stage" control and reaches the very
 * same mutation: a 1px dashed `outline-default` rectangle, a small accent `+`,
 * an accent label, and a hover fill so the large dead area admits to being
 * clickable (`web:hover:`, pure CSS — the JS hover state strands on this
 * surface, per the note in StageEditorView).
 *
 * Routing the icon through the `Icon` atom rather than rendering the raw Lucide
 * component is what retires the hardcoded colour AND the `@ts-expect-error`
 * with it: the atom takes a semantic class instead of a `color` prop.
 *
 * The lane geometry is deliberately untouched — full lane width and height, so
 * no white gap trails the last column (the SE-5396 regression its test guards).
 * @param root0 - Component props
 * @param root0.onPress - Callback function invoked when the add column button is pressed
 */
const AddColumnButtonView = ({ onPress }: AddColumnButtonViewProps) => (
  <Stack className="h-full w-72 min-w-72 shrink-0 items-stretch self-stretch">
    <Pressable
      testID={TEST_ID_PLAYER_KANBAN.ADD_COLUMN_BUTTON}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Add column"
      className="web:hover:bg-surface-raised size-full items-center justify-center rounded-lg border border-dashed border-outline-default bg-transparent p-8 data-[active=true]:scale-95"
    >
      <Stack direction="horizontal" className="items-center gap-1">
        <Icon as={Plus} size="2xs" className="text-accent-primary" />
        <Text variant="caption-strong" className="text-accent-primary">
          Add Column
        </Text>
      </Stack>
    </Pressable>
  </Stack>
);

AddColumnButtonView.displayName = "AddColumnButtonView";

export default memo(AddColumnButtonView);
